import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ChatCircleText, ArrowClockwise, MagnifyingGlass, Warning,
  User, Buildings, Timer,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * AdminCoachingResetsPage — audit trail for owner-triggered coaching resets.
 *
 * Surfaces the `coaching_reset` action from `db.audit_log` so admins can:
 *   - Spot fleets where owners aggressively reset (may indicate under-coaching)
 *   - Spot drivers reset 3+ times (may indicate the coaching isn't landing)
 *   - Read the owner-provided reason for context
 *
 * Search matches owner email, caller email, or driver name (server-side).
 */
function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function AdminCoachingResetsPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      const { data } = await client.get(`/admin/coaching-resets?${params.toString()}`);
      setRows(data.resets || []);
      setTotal(data.count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load coaching resets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(""); }, []);

  const submit = (e) => {
    e.preventDefault();
    load(q);
  };

  // Aggregate: drivers reset >=3 times = potential red flag
  const heatCount = rows.reduce((acc, r) => {
    acc[r.driver_id] = (acc[r.driver_id] || 0) + 1;
    return acc;
  }, {});
  const repeatDrivers = Object.entries(heatCount).filter(([, n]) => n >= 3).length;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8" data-testid="admin-coaching-resets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="pill pill-info mb-2 inline-flex items-center gap-1.5">
            <ChatCircleText size={11} weight="fill" /> ADMIN · COACHING AUDIT
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            Coaching Reset Log
          </h1>
          <p className="text-neutral-400 text-sm mt-2 max-w-2xl">
            Every time an owner clears a driver's SLA-coaching streak. Watch for repeat drivers — they signal coaching isn't landing.
          </p>
        </div>
        <button
          onClick={() => load(q)}
          disabled={loading}
          data-testid="admin-coaching-resets-refresh"
          className="btn-secondary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5 shrink-0"
        >
          <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="rounded-lg bg-white/[0.03] border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Resets shown</div>
          <div className="text-2xl font-mono font-bold text-white mt-1" data-testid="admin-coaching-resets-total">
            {total}
          </div>
        </div>
        <div className={`rounded-lg border p-4 ${repeatDrivers > 0 ? "border-amber-500/30 bg-amber-500/[0.06]" : "border-white/5 bg-white/[0.03]"}`}>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Repeat drivers (3+)</div>
          <div className={`text-2xl font-mono font-bold mt-1 ${repeatDrivers > 0 ? "text-amber-400" : "text-white"}`}
               data-testid="admin-coaching-resets-repeat">
            {repeatDrivers}
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Filter</div>
          <div className="text-sm text-neutral-300 mt-1 truncate" data-testid="admin-coaching-resets-filter">
            {q ? `"${q}"` : "All resets"}
          </div>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={submit} className="relative mb-6">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by owner email, caller email, or driver name…"
          data-testid="admin-coaching-resets-search"
          className="w-full bg-[#141414] border border-white/10 rounded-lg pl-9 pr-24 py-2.5 text-sm text-white placeholder:text-neutral-500 focus:border-[#60a5fa]/60 focus:outline-none"
        />
        <button
          type="submit"
          data-testid="admin-coaching-resets-search-submit"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs rounded-md bg-[#007AFF] hover:bg-[#0056b3] text-white font-semibold px-3 py-1.5"
        >
          Search
        </button>
      </form>

      {/* Table */}
      {loading && rows.length === 0 && (
        <div className="card-surface p-8 text-center text-neutral-400">Loading…</div>
      )}

      {!loading && rows.length === 0 && (
        <div className="card-surface p-8 text-center" data-testid="admin-coaching-resets-empty">
          <div className="mb-3 text-neutral-400">No coaching resets yet.</div>
          <div className="text-xs text-neutral-500">
            When an owner clears a driver's coaching streak via the Drivers page, it lands here.
          </div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="card-surface overflow-hidden" data-testid="admin-coaching-resets-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02]">
                <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left px-4 py-3">Fleet</th>
                  <th className="text-left px-4 py-3">Driver</th>
                  <th className="text-right px-4 py-3">Wiped</th>
                  <th className="text-left px-4 py-3">Reason</th>
                  <th className="text-right px-4 py-3">When</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isRepeat = heatCount[r.driver_id] >= 3;
                  return (
                    <tr
                      key={r.id}
                      data-testid={`admin-coaching-resets-row-${r.id}`}
                      className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <Buildings size={14} className="text-[#FF5A1F] mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-white truncate">{r.owner_company || "—"}</div>
                            <div className="text-[11px] text-neutral-500 font-mono truncate">
                              {r.caller_email || r.owner_email || "—"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <User size={14} className="text-neutral-400 mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <div className={`truncate ${isRepeat ? "text-amber-300 font-semibold" : "text-white"}`}>
                              {r.driver_name || "Driver"}
                            </div>
                            {isRepeat && (
                              <div className="text-[10px] uppercase tracking-widest text-amber-400 mt-0.5 inline-flex items-center gap-1"
                                   data-testid={`admin-coaching-resets-repeat-flag-${r.id}`}>
                                <Warning size={10} weight="fill" />
                                Repeat: reset {heatCount[r.driver_id]}×
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="text-white font-mono tabular-nums">{r.coaching_wiped}</div>
                        {r.escalations_wiped > 0 && (
                          <div className="text-[10px] text-red-400">{r.escalations_wiped} owner alerts</div>
                        )}
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        {r.reason ? (
                          <div className="text-xs text-neutral-300 leading-relaxed whitespace-pre-wrap break-words"
                               data-testid={`admin-coaching-resets-reason-${r.id}`}>
                            {r.reason}
                          </div>
                        ) : (
                          <span className="text-neutral-600 text-xs italic">no reason given</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="text-neutral-400 text-xs inline-flex items-center gap-1 justify-end whitespace-nowrap">
                          <Timer size={11} />
                          {fmtWhen(r.created_at)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
